import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FontFamily,
  FontWeight,
  Icon,
  type IconNames,
  IconVariant,
  Text,
} from '@gousto-internal/citrus-react'
import type { MenuRecipeSubtitleTag, StandardRecipeItem } from '@library/api-menu-experience'

type SubtitleTagRowProps = {
  recipe: Pick<StandardRecipeItem, 'displayType' | 'tagGroups'>
}

const spiceTagTypes = new Set<MenuRecipeSubtitleTag['type']>([
  'spiceNone',
  'spiceMild',
  'spiceMedium',
  'spiceHot',
])

const getSubtitleTagIconName = (
  tagType: MenuRecipeSubtitleTag['type'],
  displayType: StandardRecipeItem['displayType'],
): IconNames | null => {
  if (tagType === 'rating') {
    return 'rate_full'
  }

  if (spiceTagTypes.has(tagType)) {
    return 'chilli'
  }

  if (tagType === 'cookingTime' && displayType === 'ready-to-heat') {
    return 'microwave'
  }

  return null
}

export const SubtitleTagRow = ({ recipe }: SubtitleTagRowProps) => {
  const subtitleTags = recipe.tagGroups.subtitle ?? []

  if (subtitleTags.length === 0) {
    return null
  }

  return (
    <Box
      data-testid="recipe-tile-metadata"
      display={Display.Flex}
      alignItems={AlignItems.Center}
      gap={1}
      wrap
    >
      {subtitleTags.map((tag, index) => {
        const iconName = getSubtitleTagIconName(tag.type, recipe.displayType)

        return (
          <React.Fragment key={`${tag.type}-${tag.text}`}>
            {index > 0 ? (
              <Text
                aria-hidden="true"
                size={2}
                fontFamily={FontFamily.Book}
                fontWeight={FontWeight.Normal}
                color={Color.ColdGrey_600}
              >
                •
              </Text>
            ) : null}
            <Box
              color={Color.ColdGrey_600}
              display={Display.Flex}
              alignItems={AlignItems.Center}
              gap={1}
            >
              {iconName ? (
                <Icon aria-hidden="true" name={iconName} size={4} variant={IconVariant.Inherit} />
              ) : null}
              <Text
                size={2}
                fontFamily={FontFamily.Book}
                fontWeight={FontWeight.Normal}
                color={Color.Inherit}
              >
                {tag.text}
              </Text>
            </Box>
          </React.Fragment>
        )
      })}
    </Box>
  )
}
